/*
 * The ad offer ("1 month free, then 25% off for a year"): its build switch and its public id. No imports, so the pricing page can read it without
 * pulling in the templates. The id is public and carries no secret: the server checks it against an allow-list (Stream B). The funnel page links to
 * /pricing?offer=<OFFER_ID>; the pricing page shows the banner only for exactly this id and only in a build with the switch on.
 */

/** Build-time switch for the offer block and the pricing banner: unset = none of the offer copy is in the pages. */
export const FUNNEL_OFFER_ON = import.meta.env.VITE_FUNNEL_OFFER === 'true';

export const OFFER_ID = 'FREE_MONTH_25_YEAR';

/** The pricing link of the offer, with the ad's `utm_source` when there is one (read through readUtm, so it is trimmed and short; encoded here). */
export function offerHref(utmSource?: string): string {
  const q = new URLSearchParams({ offer: OFFER_ID });
  if (utmSource) q.set('utm_source', utmSource);
  return `/pricing?${q.toString()}`;
}

/** Where the offer waits (sessionStorage, never the URL of the sign-up page) between /pricing and /register. Only the constant is ever stored.
 *  src/lib/signup-offer.ts reads it on the register page and repeats this key and the id (a test keeps them equal): see the note there. */
export const OFFER_KEY = 'scopie_funnel_offer';

/** Keeps the offer for the register page. Storage can be unavailable (private mode, blocked): the pricing buttons also carry it in the link. */
export function rememberOffer(): void {
  try {
    window.sessionStorage.setItem(OFFER_KEY, OFFER_ID);
  } catch {
    // The link carries it then.
  }
}

/** The register link of the pricing buttons while the offer is active. */
export const offerRegisterHref = (): string => `/register?offer=${OFFER_ID}`;

/** Whether a query string carries this offer: the exact id and nothing else counts. */
export const hasOffer = (search: string): boolean => new URLSearchParams(search).get('offer') === OFFER_ID;

/** The percent off of the first year (the landing page says "25% off for your first year"). The pricing cards compute their prices from it, never type them. */
export const OFFER_PERCENT_OFF = 25;

/**
 * The price after the offer, in integer cents, from the price in cents the card shows: the percent comes off and the result is rounded half up to the
 * cent (49990 -> 37492.5 -> 37493), as Stripe rounds a percent coupon. Integer arithmetic only, so no float error can move a cent.
 */
export function discountedCents(cents: number, percentOff: number = OFFER_PERCENT_OFF): number {
  return Math.floor((cents * (100 - percentOff) + 50) / 100);
}
